/**
 * FAQ extraction for AEO / GEO
 *
 * Pulls a "Frequently Asked Questions" section out of a post's markdown so we
 * can emit FAQPage structured data that exactly mirrors the visible content.
 * Answer engines (ChatGPT, Gemini, Perplexity, Google AI Overviews) and
 * featured snippets lean heavily on FAQPage schema, so every Q&A we render on
 * the page is also exposed to machines here.
 */

export interface FaqItem {
  question: string;
  answer: string;
}

/**
 * Strip basic markdown to clean, single-line plain text suitable for JSON-LD.
 */
function markdownToPlainText(markdown: string): string {
  return markdown
    // links: [text](url) -> text
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
    // bold / italic / inline code markers
    .replace(/[*_`]+/g, "")
    // list bullets / blockquote markers at line starts
    .replace(/^\s*[-*+]\s+/gm, "")
    .replace(/^\s*\d+\.\s+/gm, "")
    .replace(/^\s*>\s?/gm, "")
    // collapse whitespace
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Extract question/answer pairs from a markdown "Frequently Asked Questions"
 * (or "FAQ"/"FAQs") section. Questions are `###` headings; the answer is every
 * line up to the next `###` question or the end of the section (the next `##`).
 *
 * Returns an empty array when the post has no FAQ section.
 */
export function extractFaqs(markdown: string): FaqItem[] {
  if (!markdown) return [];

  const lines = markdown.split("\n");

  // Find the FAQ section heading (## level).
  let start = -1;
  const headingRe = /^##\s+(?:frequently asked questions|faqs?|common questions)\s*$/i;
  for (let i = 0; i < lines.length; i++) {
    if (headingRe.test(lines[i].trim())) {
      start = i + 1;
      break;
    }
  }
  if (start === -1) return [];

  // Find the end of the section: next top-level (##, not ###) heading.
  let end = lines.length;
  for (let i = start; i < lines.length; i++) {
    const line = lines[i];
    if (/^##\s+/.test(line) && !/^###/.test(line)) {
      end = i;
      break;
    }
  }

  const section = lines.slice(start, end);
  const faqs: FaqItem[] = [];
  let currentQuestion: string | null = null;
  let answerLines: string[] = [];

  const pushCurrent = () => {
    if (currentQuestion) {
      const answer = markdownToPlainText(answerLines.join("\n"));
      if (answer) faqs.push({ question: currentQuestion, answer });
    }
  };

  for (const line of section) {
    const qMatch = line.match(/^###\s+(.+?)\s*$/);
    if (qMatch) {
      pushCurrent();
      currentQuestion = markdownToPlainText(qMatch[1]);
      answerLines = [];
    } else if (currentQuestion) {
      answerLines.push(line);
    }
  }
  pushCurrent();

  return faqs;
}

/**
 * Build a schema.org FAQPage object from extracted FAQ items.
 * Returns null when there are no FAQs so callers can skip emitting the script.
 */
export function buildFaqJsonLd(
  faqs: FaqItem[],
  pageUrl: string,
): Record<string, unknown> | null {
  if (!faqs.length) return null;
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "@id": `${pageUrl}#faq`,
    inLanguage: "en-GB",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: f.answer,
      },
    })),
  };
}
